/**
 * <ia-merch-order> keeps a collection's stored AB Tasty order fresh. The grid
 * itself is laid out on the server from the collection's metafield
 * (ia_abtasty.merch_order, written by the proxy), so nothing here touches the
 * cards. See docs/abtasty-merchandising.md.
 *
 * - data-sync (the stored order is missing or older than the freshness
 *   setting): one /merch?…&sync=1 at idle, answer dropped. The proxy stores a
 *   new order and the next visitor gets it.
 * - ?merch_refresh=1 (a merchant checking a changed strategy): the proxy is
 *   asked past every cache with sync=1&force=1, then the page reloads without
 *   the parameter and renders the order just stored.
 */

function merchUrl(endpoint, category, params) {
  const base = endpoint.replace(/\/$/, '');
  return `${base}/merch?category=${encodeURIComponent(category)}&${params}`;
}

function requestSync(endpoint, category) {
  // no-store: a copy of an earlier sync in the browser cache would never reach the proxy.
  const send = () =>
    fetch(merchUrl(endpoint, category, 'sync=1'), { priority: 'low', cache: 'no-store' }).catch(() => {});
  if (window.requestIdleCallback) window.requestIdleCallback(send, { timeout: 5000 });
  else setTimeout(send, 2000);
}

class IaMerchOrder extends HTMLElement {
  async connectedCallback() {
    const { endpoint, category } = this.dataset;
    if (!endpoint || !category) return;

    const url = new URL(window.location.href);
    if (url.searchParams.get('merch_refresh') === '1') {
      try {
        await fetch(merchUrl(endpoint, category, 'sync=1&force=1'), { cache: 'no-store' });
      } catch {
        // the reload below shows whatever is stored
      }
      // The proxy writes the metafield behind its answer, and Shopify serves a
      // cached page for an address it has seen: a pause, and a fresh address.
      url.searchParams.delete('merch_refresh');
      url.searchParams.set('merch_refreshed', String(Date.now()));
      setTimeout(() => window.location.replace(url.toString()), 1500);
      return;
    }

    if (this.hasAttribute('data-sync')) requestSync(endpoint, category);
  }
}

if (!customElements.get('ia-merch-order')) {
  customElements.define('ia-merch-order', IaMerchOrder);
}
